import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { can, isPlatformAdmin } from "@/lib/perms";
import {
  LayoutDashboard, Home, CalendarDays, AlertTriangle, Users, LogOut, Menu, X, Settings,
  ScrollText, Building2, ChevronsUpDown, Check, ShieldCheck, ClipboardCheck, Receipt, DollarSign,
  Zap, Bell, MessagesSquare, Wrench, BarChart3, Wallet, Map,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { BrandBadge } from "@/components/BrandMark";

const NAV = [
  { to: "/dashboard", icon: LayoutDashboard, text: "Dashboard", id: "dashboard" },
  { to: "/properties", icon: Home, text: "Properties", id: "properties", perm: "property:read" },
  { to: "/map", icon: Map, text: "Map", id: "map", perm: "property:read" },
  { to: "/schedule", icon: CalendarDays, text: "Work Orders", id: "schedule", perm: "job:read" },
  { to: "/inspections", icon: ClipboardCheck, text: "Inspections", id: "inspections", perm: "inspection:read" },
  { to: "/issues", icon: AlertTriangle, text: "Issues", id: "issues", perm: "issue:read" },
  { to: "/messages", icon: MessagesSquare, text: "Messages", id: "messages", perm: "message:read" },
  { to: "/invoices", icon: Receipt, text: "Invoices", id: "invoices", perm: "invoice:read" },
  { to: "/finance", icon: DollarSign, text: "Finance", id: "finance", perm: "finance:read" },
  { to: "/reports", icon: BarChart3, text: "Reports", id: "reports", perm: "analytics:read" },
  { to: "/vendors", icon: Wrench, text: "Vendors", id: "vendors", perm: "vendor:read" },
  // Field staff get their own pay view; management sees the whole payroll under Vendors.
  { to: "/my-pay", icon: Wallet, text: "My Pay", id: "my-pay", perm: "payout:read",
    hideIf: "vendor:read" },
  { to: "/automations", icon: Zap, text: "Automations", id: "automations", perm: "automation:read" },
  { to: "/team", icon: Users, text: "Team", id: "team", perm: "member:read" },
  { to: "/notifications", icon: Bell, text: "Notifications", id: "notifications" },
  { to: "/activity", icon: ScrollText, text: "Activity", id: "activity", perm: "audit:read" },
  { to: "/settings", icon: Settings, text: "Settings", id: "settings", perm: "org:read" },
];

const OrgSwitcher = ({ orgs, org, onSelect }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="org-switcher-btn"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2.5 rounded-lg bg-white/5 hover:bg-white/10 px-3 py-2.5 text-left transition-colors duration-200"
      >
        <Building2 className="h-4 w-4 text-gold shrink-0" />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-white truncate" data-testid="active-org-name">
            {org?.name || "No organization"}
          </span>
          <span className="block text-[10px] uppercase tracking-[0.16em] text-gold/80">
            {org?.role_label || "—"}
          </span>
        </span>
        <ChevronsUpDown className="h-3.5 w-3.5 text-blue-100/60 shrink-0" />
      </button>
      {open && (
        <div
          data-testid="org-switcher-menu"
          className="absolute z-50 mt-2 w-full rounded-lg border border-white/10 bg-royal-deep shadow-2xl overflow-hidden"
        >
          {orgs.map((o) => (
            <button
              key={o._id}
              type="button"
              data-testid={`org-option-${o._id}`}
              onClick={() => {
                setOpen(false);
                if (o._id !== org?._id) onSelect(o._id);
              }}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm text-blue-100/80 hover:bg-white/10 hover:text-white transition-colors duration-150"
            >
              <span className="min-w-0 flex-1 truncate">{o.name}</span>
              {o._id === org?._id && <Check className="h-3.5 w-3.5 text-gold shrink-0" />}
            </button>
          ))}
          {orgs.length === 0 && (
            <p className="px-3 py-2.5 text-xs text-blue-100/60">You have no organizations yet.</p>
          )}
        </div>
      )}
    </div>
  );
};

const AlertBell = ({ className = "" }) => {
  const [data, setData] = useState({ unread: 0, alerts: [] });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = () => api.get("/alerts").then(({ data }) => setData(data)).catch(() => {});

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => {
      clearInterval(id);
      document.removeEventListener("mousedown", onClick);
    };
  }, []);

  const openPanel = async () => {
    setOpen((v) => !v);
    if (!open && data.unread) {
      await api.post("/alerts/read", {});
      load();
    }
  };

  return (
    <div className={`relative shrink-0 ${className}`} ref={ref}>
      <button
        data-testid="alerts-bell"
        onClick={openPanel}
        aria-label="Alerts"
        className="relative flex h-11 w-11 items-center justify-center rounded-md text-royal hover:bg-royal/5 transition-colors duration-150"
      >
        <Bell className="h-5 w-5" />
        {data.unread > 0 && (
          <span
            data-testid="alerts-unread-count"
            className="absolute top-1.5 right-1.5 min-w-[18px] rounded-full bg-gold px-1 text-[10px] font-bold leading-[18px] text-royal-deep"
          >
            {data.unread}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="alerts-panel"
          className="absolute right-0 z-50 mt-2 w-[300px] max-w-[calc(100vw-2rem)] rounded-lg border border-border bg-white shadow-xl overflow-hidden"
        >
          {data.alerts.length === 0 ? (
            <p className="px-4 py-4 text-xs text-muted-foreground">Nothing needs your attention right now.</p>
          ) : (
            <div className="max-h-[320px] overflow-y-auto divide-y divide-border">
              {data.alerts.map((a) => (
                <div key={a._id} className="px-4 py-3" data-testid={`alert-${a._id}`}>
                  <p className="text-sm font-semibold text-royal-deep">{a.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{a.body}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {new Date(a.created_at).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function AppLayout() {
  const { user, org, orgs, logout, switchOrg } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const items = NAV.filter((n) => (!n.perm || can(user, n.perm)) && !(n.hideIf && can(user, n.hideIf)));

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-background">
      <aside
        id="app-sidebar"
        data-testid="sidebar"
        className={cn(
          "fixed inset-y-0 left-0 z-40 w-[272px] max-w-[85vw] bg-royal-deep sidebar-grain flex flex-col overflow-y-auto overscroll-contain safe-top transition-transform duration-300 ease-out lg:translate-x-0",
          open ? "translate-x-0 shadow-2xl" : "-translate-x-full"
        )}
      >
        <div className="px-5 pt-7 pb-5">
          <div className="flex items-center gap-2.5">
            <BrandBadge />
            <div>
              <p className="font-display text-white text-lg font-semibold leading-none">Cornerstone</p>
              <p className="text-gold text-[11px] tracking-[0.22em] uppercase mt-1">Ops</p>
            </div>
            <button
              data-testid="sidebar-close-btn"
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close navigation"
              className="lg:hidden ml-auto -mr-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-blue-100/80 hover:text-white active:bg-white/10 touch-manipulation select-none transition-colors duration-150"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="h-px gold-rule mt-5 mb-4" />
          <OrgSwitcher orgs={orgs} org={org} onSelect={(id) => switchOrg(id)} />
        </div>

        <nav className="flex-1 px-3 space-y-1">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.id}`}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-md px-3 min-h-[48px] py-3 text-sm font-medium touch-manipulation select-none transition-colors duration-200 active:bg-white/15",
                  isActive
                    ? "bg-white/10 text-white border-l-2 border-gold"
                    : "text-blue-100/70 hover:text-white hover:bg-white/5 border-l-2 border-transparent"
                )
              }
            >
              <item.icon className="h-[18px] w-[18px]" />
              {item.text}
            </NavLink>
          ))}
          {isPlatformAdmin(user) && (
            <NavLink
              to="/platform"
              data-testid="nav-platform"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-md px-3 min-h-[48px] py-3 text-sm font-medium transition-colors duration-200",
                  isActive
                    ? "bg-gold/20 text-white border-l-2 border-gold"
                    : "text-gold/80 hover:text-gold hover:bg-white/5 border-l-2 border-transparent"
                )
              }
            >
              <ShieldCheck className="h-[18px] w-[18px]" /> Platform
            </NavLink>
          )}
        </nav>

        <div className="p-4 mt-auto">
          <div className="rounded-lg bg-white/5 p-3">
            <p className="text-white text-sm font-medium truncate">{user?.name}</p>
            <p className="text-gold text-xs">{user?.role_label || "Member"}</p>
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="mt-3 w-full flex items-center justify-center gap-2 rounded-md bg-gold/90 hover:bg-gold active:bg-gold px-3 min-h-[44px] text-xs font-semibold text-royal-deep touch-manipulation select-none transition-colors duration-200"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
          <div className="mt-4 px-1" data-testid="sidebar-verse">
            <div className="h-px gold-rule mb-3" />
            <p className="text-gold text-[10px] tracking-[0.18em] uppercase font-semibold">Psalm 24:1, NLT</p>
            <p className="text-gold/80 text-xs italic leading-relaxed mt-1.5">
              "The earth is the LORD'S, and everything in it."
            </p>
          </div>
        </div>
      </aside>

      {open && (
        <div
          data-testid="sidebar-overlay"
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}

      <div className="flex-1 lg:ml-[272px] min-w-0">
        <header className="safe-top sticky top-0 z-20 border-b border-border bg-white/95 backdrop-blur-md px-3 sm:px-8">
          <div className="min-h-16 flex items-center gap-2 py-2">
            <button
              data-testid="menu-toggle"
              type="button"
              className="lg:hidden -ml-1 flex h-12 w-12 shrink-0 items-center justify-center rounded-md text-royal touch-manipulation select-none active:bg-royal/10 transition-colors duration-150"
              onClick={() => setOpen(true)}
              aria-label="Open navigation"
              aria-expanded={open}
              aria-controls="app-sidebar"
            >
              <Menu className="h-6 w-6" />
            </button>
            <div className="min-w-0 flex-1">
              <p
                className="font-display text-sm sm:text-base md:text-lg font-semibold text-royal-deep leading-snug break-words"
                data-testid="header-org-name"
              >
                {org?.name || "Cornerstone Ops"}
              </p>
              <p className="text-[11px] sm:text-xs text-muted-foreground leading-snug break-words">
                {org?.tagline || "Property operations platform"}
              </p>
            </div>
            {user?.impersonated && (
              <span
                data-testid="impersonation-banner"
                className="ml-auto rounded-full border border-red-300 bg-red-50 px-3 py-1.5 text-[11px] font-semibold text-red-700"
              >
                Platform admin view — actions are audited
              </span>
            )}
            <AlertBell className={user?.impersonated ? "" : "ml-auto"} />
          </div>
        </header>
        <main className="p-4 sm:p-8 pb-[calc(2rem+env(safe-area-inset-bottom,0px))] max-w-[1400px]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
